import React__default, { useRef, useCallback, useEffect, useState } from 'react';
import { SurveyThemeWrapper } from '@sm/question-ui/theme-respondent';
import { d as declareVisualization, i as isLocalError, b as isRequired, p as providerFactory } from './utils-98184220.js';
import { commentBoxRespondentSurveyValidation, singleTextboxRespondentSurveyValidation } from '@sm/question-definitions';
import { CommentBox, SingleTextbox } from '@sm/question-ui/respondent-survey';
export { S as SingleQuestionController } from './controllers-b9e161e1.js';
import './vendor-4c16529b.js';

const useWithValidatorRegistration = (question, value, touched, registerValidator, unregisterValidator, questionTypeSpecificValidation) => {
  const {
    id
  } = question;
  const validationFunction = useRef();
  validationFunction.current = useCallback(forceValidation => {
    return questionTypeSpecificValidation(question, value, touched || forceValidation);
  }, [question, value, touched, questionTypeSpecificValidation]);
  useEffect(() => {
    if (registerValidator) {
      registerValidator(id, [forceValidation => {
        if (!validationFunction.current) {
          return null;
        }

        return validationFunction.current(forceValidation);
      }]);
    }

    return () => unregisterValidator === null || unregisterValidator === void 0 ? void 0 : unregisterValidator(id);
  }, [id, registerValidator, unregisterValidator]);
};

function useFieldIsDirty(active) {
  const [fieldDirty, setFieldIsDirty] = useState(false);
  useEffect(() => {
    setFieldIsDirty(false);
  }, [active]);
  return [fieldDirty, setFieldIsDirty];
}

const useShowOkButton = (active, dirty) => {
  const [showOkButton, setShowOkButton] = useState(false);
  useEffect(() => {
    if (!active) {
      setShowOkButton(false);
    } else if (dirty) {
      setShowOkButton(true);
    }
  }, [active, dirty, setShowOkButton]);
  return showOkButton;
}; // Visualization for Question of type CommentBox


const RespondentCommentBoxVisualization = ({
  upstreamActions,
  question,
  settings: {
    survey
  },
  toExternalQuestion,
  displayOptions
}) => {
  var _a, _b;

  const {
    id,
    questionNumber,
    title,
    layout,
    disabled
  } = question;
  const {
    active,
    errors: upstreamErrors,
    registerValidator,
    unregisterValidator
  } = displayOptions || {};
  const upstreamAnswer = displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.answer;
  const padding = (_a = layout === null || layout === void 0 ? void 0 : layout.spacing) !== null && _a !== void 0 ? _a : {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0
  };
  const error = (upstreamErrors !== null && upstreamErrors !== void 0 ? upstreamErrors : []).find(isLocalError);
  const [answer, setAnswer] = useState({
    value: (_b = upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.value) !== null && _b !== void 0 ? _b : '',
    touched: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.touched),
    isDirty: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.isDirty),
    questionId: id
  });
  useWithValidatorRegistration(question, answer.value, answer.touched, registerValidator, unregisterValidator, commentBoxRespondentSurveyValidation);
  const [fieldDirty, setFieldIsDirty] = useFieldIsDirty(!!active);
  const showOkButton = useShowOkButton(!!active, fieldDirty);
  const onLeaveQuestion = useCallback(() => {
    if (upstreamActions.onLeaveQuestion) {
      upstreamActions.onLeaveQuestion(toExternalQuestion(question), answer);
    }
  }, [question, upstreamActions, answer, toExternalQuestion]);
  const surveyModeOptions = survey.isClassic ? {
    variant: 'classic'
  } : {
    variant: 'auto-scroll',
    active: !!active,
    showOkButton,
    onSubmit: displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.onSubmit
  };
  return React__default.createElement(CommentBox, Object.assign({}, surveyModeOptions, {
    title,
    padding,
    disabled,
    survey
  }, {
    questionId: id,
    questionNumber: Number(questionNumber),
    required: isRequired(question),
    error: error === null || error === void 0 ? void 0 : error.message,
    value: answer === null || answer === void 0 ? void 0 : answer.value,
    okLabel: survey.okLabel,
    onLeave: onLeaveQuestion,
    onChange: value => {
      setFieldIsDirty(true);
      setAnswer({
        touched: true,
        questionId: id,
        value,
        isDirty: value !== (upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.value)
      });
    }
  }));
};

var AnswerCommentBoxVisualization = declareVisualization(['comment_box'], ['respondent-survey'], RespondentCommentBoxVisualization); // Visualization for Question of type SingleTextbox

const RespondentSingleTextboxVisualization = ({
  upstreamActions,
  question,
  settings: {
    survey
  },
  toExternalQuestion,
  displayOptions
}) => {
  var _a, _b;

  const {
    id,
    questionNumber,
    title,
    layout,
    disabled
  } = question;
  const {
    active,
    errors: upstreamErrors,
    registerValidator,
    unregisterValidator
  } = displayOptions || {};
  const upstreamAnswer = displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.answer;
  const padding = (_a = layout === null || layout === void 0 ? void 0 : layout.spacing) !== null && _a !== void 0 ? _a : {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0
  };
  const error = (upstreamErrors !== null && upstreamErrors !== void 0 ? upstreamErrors : []).find(isLocalError);
  const [answer, setAnswer] = useState({
    value: (_b = upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.value) !== null && _b !== void 0 ? _b : '',
    touched: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.touched),
    isDirty: !!(upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.isDirty),
    questionId: id
  });
  useWithValidatorRegistration(question, answer.value, answer.touched, registerValidator, unregisterValidator, singleTextboxRespondentSurveyValidation);
  /**
   * Determines if the field is dirty (changed) internally (not for validation)
   * The OK Button is visible when change is made to the field only. The dirty
   * state resets (false) when activation of the question changes.
   */

  const [fieldDirty, setFieldIsDirty] = useFieldIsDirty(!!active);
  const showOkButton = useShowOkButton(!!active, fieldDirty);
  const onLeaveQuestion = useCallback(() => {
    if (upstreamActions.onLeaveQuestion) {
      upstreamActions.onLeaveQuestion(toExternalQuestion(question), answer);
    }
  }, [question, upstreamActions, answer, toExternalQuestion]);
  const surveyModeOptions = survey.isClassic ? {
    variant: 'classic'
  } : {
    variant: 'auto-scroll',
    active: !!active,
    showOkButton,
    onSubmit: displayOptions === null || displayOptions === void 0 ? void 0 : displayOptions.onSubmit
  };
  return React__default.createElement(SingleTextbox, Object.assign({}, surveyModeOptions, {
    title,
    padding,
    disabled,
    survey
  }, {
    questionId: id,
    questionNumber: Number(questionNumber),
    required: isRequired(question),
    error: error === null || error === void 0 ? void 0 : error.message,
    value: answer.value,
    okLabel: survey.okLabel,
    onLeave: onLeaveQuestion,
    onChange: value => {
      setFieldIsDirty(true);
      setAnswer({
        touched: true,
        questionId: id,
        value,
        isDirty: value !== (upstreamAnswer === null || upstreamAnswer === void 0 ? void 0 : upstreamAnswer.value)
      });
    }
  }));
};

var AnswerSingleTextboxVisualization = declareVisualization(['single_textbox'], ['respondent-survey'], RespondentSingleTextboxVisualization);
var visualizationList = [AnswerSingleTextboxVisualization, AnswerCommentBoxVisualization]; // Creates the helper to store the configuration

const ProviderHelper = providerFactory(visualizationList); // Declares the Provider Component itself, with the proper API

const RespondentCapabilityProvider = ({
  upstreamActions,
  settings,
  children,
  renderProps
}) => {
  // here you can keep capability-specific state, translate actions or make other modifications
  // that are appropriate to your capability-specific behaviour
  return React__default.createElement(ProviderHelper, {
    value: {
      capability: 'respondent-survey',
      upstreamActions,
      renderProps,
      settings
    }
  }, React__default.createElement(SurveyThemeWrapper, null, children));
};

export { RespondentCapabilityProvider, visualizationList as RespondentVisualizations };